import React, { useEffect, Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import "@/App.css";

import { AuthProvider, useAuth } from "@/lib/auth";
import { MusicPlayerProvider } from "@/lib/MusicPlayerContext";
import { I18nProvider } from "@/lib/i18n";
import IncomingCallListener from "@/components/IncomingCallListener";
import BroadcastListener from "@/components/BroadcastListener";

// Eagerly loaded: the pages a first-time visitor sees before login. Keeps
// the initial bundle tiny so LCP stays quick on cellular.
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import RegisterMember from "@/pages/RegisterMember";
import Dashboard from "@/pages/Dashboard";

// ---- Lazy chunks — split off everything else so it downloads only
// when the user actually opens that page. Under Suspense we render a
// dead-simple spinner so route transitions stay snappy on 4G.
const Announcements = lazy(() => import("@/pages/Announcements"));
const Yearbook = lazy(() => import("@/pages/Yearbook"));
const Board = lazy(() => import("@/pages/Board"));
const Pastor = lazy(() => import("@/pages/Pastor"));
const FirstElder = lazy(() => import("@/pages/FirstElder"));
const Elders = lazy(() => import("@/pages/Elders"));
const ContactDeveloper = lazy(() => import("@/pages/ContactDeveloper"));
const SuperDeveloperSettings = lazy(() => import("@/pages/super/SuperDeveloperSettings"));
const SuperDeveloperInbox = lazy(() => import("@/pages/super/SuperDeveloperInbox"));
const PublicAboutDeveloper = lazy(() => import("@/pages/public/PublicAboutDeveloper"));
const Departments = lazy(() => import("@/pages/Departments"));
const Live = lazy(() => import("@/pages/Live"));
const Prayer = lazy(() => import("@/pages/Prayer"));
const Bible = lazy(() => import("@/pages/Bible"));
const Songbook = lazy(() => import("@/pages/Songbook"));
const Tithes = lazy(() => import("@/pages/Tithes"));
const Carpool = lazy(() => import("@/pages/Carpool"));
const Members = lazy(() => import("@/pages/Members"));
const MemberProfile = lazy(() => import("@/pages/MemberProfile"));
const Chat = lazy(() => import("@/pages/Chat"));
const ChatConversation = lazy(() => import("@/pages/ChatConversation"));
const About = lazy(() => import("@/pages/About"));
const Visit = lazy(() => import("@/pages/Visit"));
const More = lazy(() => import("@/pages/More"));
const Gallery = lazy(() => import("@/pages/Gallery"));
const PaymentSuccess = lazy(() => import("@/pages/PaymentSuccess"));
const Events = lazy(() => import("@/pages/Events"));
const Sermons = lazy(() => import("@/pages/Sermons"));
const Notifications = lazy(() => import("@/pages/Notifications"));
const SabbathSet = lazy(() => import("@/pages/SabbathSet"));
const SabbathSetDetail = lazy(() =>
  import("@/pages/SabbathSet").then((m) => ({ default: m.SabbathSetDetail })),
);
const Birthdays = lazy(() => import("@/pages/Birthdays"));

// Admin console — heaviest chunk, only loaded when a main_admin opens it.
const SuperAdmin = lazy(() => import("@/pages/SuperAdmin"));
const SuperPlatformSettings = lazy(() => import("@/pages/super/SuperPlatformSettings"));
const ServerHealth = lazy(() => import("@/pages/super/ServerHealth"));
const AdminPanel = lazy(() => import("@/pages/admin/AdminPanel"));
const AdminAnnouncements = lazy(() => import("@/pages/admin/AdminAnnouncements"));
const AdminPastor = lazy(() => import("@/pages/admin/AdminPastor"));
const AdminFirstElder = lazy(() => import("@/pages/admin/AdminFirstElder"));
const AdminDepartments = lazy(() => import("@/pages/admin/AdminDepartments"));
const AdminBoard = lazy(() => import("@/pages/admin/AdminBoard"));
const AdminMembers = lazy(() => import("@/pages/admin/AdminMembers"));
const AdminBranding = lazy(() => import("@/pages/admin/AdminBranding"));
const AdminFeatures = lazy(() => import("@/pages/admin/AdminFeatures"));
const AdminTileOrder = lazy(() => import("@/pages/admin/AdminTileOrder"));
const AdminSubscription = lazy(() => import("@/pages/admin/AdminSubscription"));
const SubscriptionSuccess = lazy(() => import("@/pages/admin/SubscriptionSuccess"));
const AdminEvents = lazy(() => import("@/pages/admin/AdminEvents"));
const AdminSermons = lazy(() => import("@/pages/admin/AdminSermons"));
const AdminSongbook = lazy(() => import("@/pages/admin/AdminSongbook"));
const AdminFinance = lazy(() => import("@/pages/admin/AdminFinance"));
const AdminGallery = lazy(() => import("@/pages/admin/AdminGallery"));
const AdminSabbathSets = lazy(() => import("@/pages/admin/AdminSabbathSets"));
const AdminRoster = lazy(() => import("@/pages/admin/AdminRoster"));
const AdminPendingMembers = lazy(() => import("@/pages/admin/AdminPendingMembers"));
const AdminAudio = lazy(() => import("@/pages/admin/AdminAudio"));

// Public (unauthenticated) read-only pages — lazy so the bundle stays
// slim for signed-in members who never visit them.
const PublicAnnouncements = lazy(() => import("@/pages/public/PublicAnnouncements"));
const PublicEvents = lazy(() => import("@/pages/public/PublicEvents"));
const PublicSermons = lazy(() => import("@/pages/public/PublicSermons"));
const PublicSongbook = lazy(() => import("@/pages/public/PublicSongbook"));
const PublicBoard = lazy(() => import("@/pages/public/PublicBoard"));
const PublicPastor = lazy(() => import("@/pages/public/PublicPastor"));
const PublicFirstElder = lazy(() => import("@/pages/public/PublicFirstElder"));
const PublicDepartments = lazy(() => import("@/pages/public/PublicDepartments"));
const PublicBible = lazy(() => import("@/pages/public/PublicBible"));
const PublicLive = lazy(() => import("@/pages/public/PublicLive"));
const PublicTithes = lazy(() => import("@/pages/public/PublicTithes"));
const PublicPaymentSuccess = lazy(() => import("@/pages/public/PublicPaymentSuccess"));
const PublicPrayer = lazy(() => import("@/pages/public/PublicPrayer"));
const PublicCarpool = lazy(() => import("@/pages/public/PublicCarpool"));
const PublicYearbook = lazy(() => import("@/pages/public/PublicYearbook"));
const PublicSabbathLessons = lazy(() => import("@/pages/public/PublicSabbathLessons"));
const PublicAbout = lazy(() => import("@/pages/public/PublicAbout"));
const PublicVisit = lazy(() => import("@/pages/public/PublicVisit"));
const PublicGallery = lazy(() => import("@/pages/public/PublicGallery"));

const RouteFallback = () => (
  <div className="mobile-shell grid place-items-center text-stone-400 text-sm py-16" data-testid="route-loading">
    <div className="w-6 h-6 border-2 border-stone-200 border-t-stone-500 rounded-full animate-spin" />
  </div>
);

function Protected({ children, allow }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="mobile-shell grid place-items-center text-stone-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (allow && !allow.includes(user.role)) return <Navigate to="/dashboard" replace />;
  return children;
}

function MemberRoute({ children }) {
  // Members + admins + pastor — basically any logged-in non-super user
  const { user, church, loading } = useAuth();
  if (loading) return <div className="mobile-shell grid place-items-center text-stone-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "super_admin") return <Navigate to="/super-admin" replace />;
  if (!church) return <Navigate to="/login" replace />;
  if (church.status !== "approved") return <Navigate to="/pending" replace />;
  const subActive = ["active", "trialing"].includes(church.subscription_status);
  if (!subActive) return <Navigate to="/inactive" replace />;
  return children;
}

function PendingScreen() {
  const { logout, church, user } = useAuth();
  return (
    <div className="mobile-shell p-8 text-center" data-testid="pending-page">
      <div className="text-5xl mb-4">⏳</div>
      <h1 className="font-serif-display text-2xl">Pending approval</h1>
      <p className="text-sm text-stone-500 mt-2 leading-relaxed">
        {church?.name} is awaiting super-admin approval.<br />
        Typical review time is under 24 hours.
      </p>
      <button
        onClick={() => { logout(); window.location.href = "/"; }}
        className="mt-6 text-xs text-stone-500 underline"
        data-testid="pending-logout"
      >
        Sign out
      </button>
    </div>
  );
}

function InactiveScreen() {
  const { logout, church, user } = useAuth();
  const isAdmin = user?.role === "main_admin";
  return (
    <div className="mobile-shell p-8 text-center" data-testid="inactive-page">
      <div className="text-5xl mb-4">🚧</div>
      <h1 className="font-serif-display text-2xl">Subscription inactive</h1>
      <p className="text-sm text-stone-500 mt-2 leading-relaxed">
        {church?.name} doesn&apos;t have an active subscription right now.<br />
        {isAdmin
          ? "Please contact your platform administrator to activate."
          : "Please ask your church administrator."}
      </p>
      <button
        onClick={() => { logout(); window.location.href = "/"; }}
        className="mt-6 text-xs text-stone-500 underline"
        data-testid="inactive-logout"
      >
        Sign out
      </button>
    </div>
  );
}

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <div className="mobile-shell p-8 text-stone-500">Loading…</div>;
  if (!user) return <Landing />;
  if (user.role === "super_admin") return <Navigate to="/super-admin" replace />;
  return <Navigate to="/dashboard" replace />;
}

/**
 * Bridges service-worker `postMessage({type:"navigate", url})` events to
 * React Router so a push-notification tap opens the deep-link inside
 * the SPA instead of a full reload.
 */
function ServiceWorkerNavBridge() {
  const navigate = useNavigate();
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    const onMessage = (e) => {
      const data = e?.data || {};
      if (data.type === "navigate" && typeof data.url === "string") {
        try {
          const u = new URL(data.url, window.location.origin);
          if (u.origin === window.location.origin) {
            navigate(u.pathname + u.search + u.hash);
          }
        } catch (_) { /* ignore */ }
      }
    };
    navigator.serviceWorker.addEventListener("message", onMessage);
    return () => navigator.serviceWorker.removeEventListener("message", onMessage);
  }, [navigate]);
  return null;
}

function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <MusicPlayerProvider>
        <BrowserRouter>
          <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<RootRedirect />} />
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/register-member" element={<RegisterMember />} />
            {/* /register-church removed — church creation is super-admin only */}

            {/* Public read-only pages (no auth) */}
            <Route path="/public/announcements" element={<PublicAnnouncements />} />
            <Route path="/public/events" element={<PublicEvents />} />
            <Route path="/public/sermons" element={<PublicSermons />} />
            <Route path="/public/songbook" element={<PublicSongbook />} />
            <Route path="/public/board" element={<PublicBoard />} />
            <Route path="/public/pastor" element={<PublicPastor />} />
            <Route path="/public/first-elder" element={<PublicFirstElder />} />
            <Route path="/public/departments" element={<PublicDepartments />} />
            <Route path="/public/bible" element={<PublicBible />} />
            <Route path="/public/live" element={<PublicLive />} />
            <Route path="/public/giving" element={<PublicTithes />} />
            <Route path="/public/payment-success" element={<PublicPaymentSuccess />} />
            <Route path="/public/prayer" element={<PublicPrayer />} />
            <Route path="/public/carpool" element={<PublicCarpool />} />
            <Route path="/public/yearbook" element={<PublicYearbook />} />
            <Route path="/public/sabbath-lessons" element={<PublicSabbathLessons />} />
            <Route path="/public/about" element={<PublicAbout />} />
            <Route path="/public/about-developer" element={<PublicAboutDeveloper />} />
            <Route path="/public/visit" element={<PublicVisit />} />
            <Route path="/public/gallery" element={<PublicGallery />} />
            <Route path="/public/gallery/album/:albumId" element={<PublicGallery />} />

            <Route path="/pending" element={<Protected><PendingScreen /></Protected>} />
            <Route path="/inactive" element={<Protected><InactiveScreen /></Protected>} />

            {/* Super admin */}
            <Route path="/super-admin" element={<Protected allow={["super_admin"]}><SuperAdmin /></Protected>} />
            <Route path="/super-admin/platform-features" element={<Protected allow={["super_admin"]}><SuperPlatformSettings /></Protected>} />
            <Route path="/super-admin/server-health" element={<Protected allow={["super_admin"]}><ServerHealth /></Protected>} />
            <Route path="/super-admin/developer-settings" element={<Protected allow={["super_admin"]}><SuperDeveloperSettings /></Protected>} />
            <Route path="/super-admin/messages" element={<Protected allow={["super_admin"]}><SuperDeveloperInbox /></Protected>} />

            {/* Main admin */}
            <Route path="/admin" element={<Protected allow={["main_admin"]}><AdminPanel /></Protected>} />
            <Route path="/admin/announcements" element={<Protected allow={["main_admin"]}><AdminAnnouncements /></Protected>} />
            <Route path="/admin/pastor" element={<Protected allow={["main_admin"]}><AdminPastor /></Protected>} />
            <Route path="/admin/first-elder" element={<Protected allow={["main_admin"]}><AdminFirstElder /></Protected>} />
            <Route path="/admin/departments" element={<Protected allow={["main_admin", "dept_admin"]}><AdminDepartments /></Protected>} />
            <Route path="/admin/board" element={<Protected allow={["main_admin"]}><AdminBoard /></Protected>} />
            <Route path="/admin/members" element={<Protected allow={["main_admin"]}><AdminMembers /></Protected>} />
            <Route path="/admin/branding" element={<Protected allow={["main_admin"]}><AdminBranding /></Protected>} />
            <Route path="/admin/features" element={<Protected allow={["main_admin"]}><AdminFeatures /></Protected>} />
            <Route path="/admin/tile-order" element={<Protected allow={["main_admin"]}><AdminTileOrder /></Protected>} />
            <Route path="/admin/subscription" element={<Protected allow={["main_admin"]}><AdminSubscription /></Protected>} />
            <Route path="/admin/events" element={<Protected allow={["main_admin"]}><AdminEvents /></Protected>} />
            <Route path="/admin/sermons" element={<Protected allow={["main_admin"]}><AdminSermons /></Protected>} />
            <Route path="/admin/songbook" element={<Protected allow={["main_admin"]}><AdminSongbook /></Protected>} />
            <Route path="/admin/finance" element={<Protected allow={["main_admin", "treasurer"]}><AdminFinance /></Protected>} />
            <Route path="/admin/gallery" element={<Protected allow={["main_admin"]}><AdminGallery /></Protected>} />
            <Route path="/admin/sabbath-sets" element={<Protected allow={["main_admin", "worship_leader"]}><AdminSabbathSets /></Protected>} />
            <Route path="/admin/roster" element={<Protected allow={["main_admin"]}><AdminRoster /></Protected>} />
            <Route path="/admin/pending" element={<Protected allow={["main_admin"]}><AdminPendingMembers /></Protected>} />
            <Route path="/admin/audio" element={<Protected allow={["main_admin"]}><AdminAudio /></Protected>} />
            <Route path="/subscription/success" element={<Protected allow={["main_admin"]}><SubscriptionSuccess /></Protected>} />

            {/* Member feature routes */}
            <Route path="/dashboard" element={<MemberRoute><Dashboard /></MemberRoute>} />
            <Route path="/announcements" element={<MemberRoute><Announcements /></MemberRoute>} />
            <Route path="/yearbook" element={<MemberRoute><Yearbook /></MemberRoute>} />
            <Route path="/board" element={<MemberRoute><Board /></MemberRoute>} />
            <Route path="/pastor" element={<MemberRoute><Pastor /></MemberRoute>} />
            <Route path="/first-elder" element={<MemberRoute><FirstElder /></MemberRoute>} />
            <Route path="/elders" element={<MemberRoute><Elders /></MemberRoute>} />
            <Route path="/contact-developer" element={<MemberRoute><ContactDeveloper /></MemberRoute>} />
            <Route path="/departments" element={<MemberRoute><Departments /></MemberRoute>} />
            <Route path="/live" element={<MemberRoute><Live /></MemberRoute>} />
            <Route path="/prayer" element={<MemberRoute><Prayer /></MemberRoute>} />
            <Route path="/bible" element={<MemberRoute><Bible /></MemberRoute>} />
            <Route path="/songbook" element={<MemberRoute><Songbook /></MemberRoute>} />
            <Route path="/tithes" element={<MemberRoute><Tithes /></MemberRoute>} />
            <Route path="/carpool" element={<MemberRoute><Carpool /></MemberRoute>} />
            <Route path="/members" element={<MemberRoute><Members /></MemberRoute>} />
            <Route path="/members/:userId" element={<MemberRoute><MemberProfile /></MemberRoute>} />
            <Route path="/chat" element={<MemberRoute><Chat /></MemberRoute>} />
            <Route path="/chat/:conversationId" element={<MemberRoute><ChatConversation /></MemberRoute>} />
            <Route path="/about" element={<MemberRoute><About /></MemberRoute>} />
            <Route path="/visit" element={<MemberRoute><Visit /></MemberRoute>} />
            <Route path="/gallery" element={<MemberRoute><Gallery /></MemberRoute>} />
            <Route path="/gallery/album/:albumId" element={<MemberRoute><Gallery /></MemberRoute>} />
            <Route path="/events" element={<MemberRoute><Events /></MemberRoute>} />
            <Route path="/sermons" element={<MemberRoute><Sermons /></MemberRoute>} />
            <Route path="/notifications" element={<MemberRoute><Notifications /></MemberRoute>} />
            <Route path="/sabbath-set" element={<MemberRoute><SabbathSet /></MemberRoute>} />
            <Route path="/sabbath-set/:setId" element={<MemberRoute><SabbathSetDetail /></MemberRoute>} />
            <Route path="/birthdays" element={<MemberRoute><Birthdays /></MemberRoute>} />
            <Route path="/more" element={<MemberRoute><More /></MemberRoute>} />
            <Route path="/payment-success" element={<MemberRoute><PaymentSuccess /></MemberRoute>} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </Suspense>
          <IncomingCallListener />
          <BroadcastListener />
          <ServiceWorkerNavBridge />
          <Toaster position="top-center" />
        </BrowserRouter>
        </MusicPlayerProvider>
      </AuthProvider>
    </I18nProvider>
  );
}

export default App;
